import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, Building2, MapPin, X, ChevronDown, AlertTriangle } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CompanyResult {
  nom_complet: string;
  nom_raison_sociale?: string | null;
  siren: string;
  siege: {
    adresse: string;
    code_postal: string;
    commune: string;
    siret: string;
    departement?: string | null;
    region?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
  activite_principale?: string | null;
  libelle_activite_principale?: string | null;
  tranche_effectif_salarie?: string | null;
  nombre_etablissements?: number | null;
  /** 'A' = actif, 'F' = fermé */
  etat_administratif?: 'A' | 'F' | string | null;
  /** 'GE' | 'ETI' | 'PME' | 'MI' */
  categorie_entreprise?: string | null;
  /** Format ISO : '1984-03-12' */
  date_creation?: string | null;
  dirigeants?: {
    nom?: string;
    prenoms?: string;
    qualite?: string;
  }[] | null;
}

interface ApiResponse {
  results: CompanyResult[];
  total_results: number;
}

interface CompanyAutocompleteProps {
  /** Called when the user selects a company from the dropdown */
  onSelect: (company: CompanyResult) => void;
  /** Pre-fill the input (e.g. when editing an existing client) */
  defaultValue?: string;
  /** Additional wrapper class */
  className?: string;
  /** Input placeholder */
  placeholder?: string;
  /** Whether to show a clear button when a company is selected */
  clearable?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCity(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSiren(siren: string): string {
  return siren.replace(/(\d{3})(?=\d)/g, '$1 ');
}

const CATEGORIE_LABELS: Record<string, { label: string; color: string }> = {
  GE:  { label: 'Grande Entreprise', color: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
  ETI: { label: 'ETI',               color: 'bg-violet-50 text-violet-600 border-violet-100' },
  PME: { label: 'PME',               color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  MI:  { label: 'Micro',             color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function CategorieBadge({ code }: { code: string }) {
  const cfg = CATEGORIE_LABELS[code];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function CompanyAutocomplete({
  onSelect,
  defaultValue = '',
  className = '',
  placeholder = 'Ex: Canal+, Capgemini…',
  clearable = true,
}: CompanyAutocompleteProps) {
  const [query, setQuery] = useState(defaultValue);
  const [results, setResults] = useState<CompanyResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState<CompanyResult | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced API search
  const search = useCallback((value: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (value.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      setHasSearched(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setIsLoading(true);
      setHasSearched(true);
      try {
        const url = `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(value.trim())}&per_page=10`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('API error');
        const data: ApiResponse = await res.json();
        setResults(data.results ?? []);
        setIsOpen(true);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setQuery(value);
    setSelected(null);
    search(value);
  }

  function handleSelect(company: CompanyResult) {
    setQuery(company.nom_complet);
    setSelected(company);
    setIsOpen(false);
    setResults([]);
    onSelect(company);
  }

  function handleClear() {
    setQuery('');
    setSelected(null);
    setResults([]);
    setIsOpen(false);
    setHasSearched(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }

  const showEmpty = isOpen && hasSearched && !isLoading && results.length === 0;

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Input wrapper */}
      <div
        className={`
          flex items-center gap-2 px-3 py-2.5
          border rounded-xl text-sm transition-all duration-150
          ${selected
            ? 'border-amber-400 bg-amber-50/40 ring-2 ring-amber-500/20'
            : 'border-slate-200 bg-white focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500'
          }
        `}
      >
        {isLoading ? (
          <Loader2 size={16} className="text-amber-500 shrink-0 animate-spin" />
        ) : (
          <Search size={16} className="text-slate-400 shrink-0" />
        )}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="flex-1 outline-none bg-transparent text-slate-800 placeholder:text-slate-400 text-sm min-w-0"
        />

        {clearable && query && (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 p-0.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X size={14} />
          </button>
        )}

        {!query && (
          <ChevronDown size={14} className="text-slate-300 shrink-0" />
        )}
      </div>

      {/* Dropdown panel */}
      {(isOpen || showEmpty) && (
        <div
          className="
            absolute z-50 mt-1.5 w-full
            bg-white border border-slate-200 rounded-xl shadow-lg
            overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150
          "
        >
          {/* No results */}
          {showEmpty && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
              <Building2 size={15} className="shrink-0" />
              Aucune entreprise trouvée pour « {query} »
            </div>
          )}

          {/* Results list */}
          {results.length > 0 && (
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-50">
              {results.map((company) => {
                const city = company.siege?.commune
                  ? formatCity(company.siege.commune)
                  : null;
                const postalCode = company.siege?.code_postal ?? null;
                const isClosed = company.etat_administratif === 'F';

                return (
                  <li key={company.siren}>
                    <button
                      type="button"
                      onClick={() => handleSelect(company)}
                      className={`
                        w-full text-left px-4 py-3
                        transition-colors duration-100
                        focus:outline-none
                        ${isClosed
                          ? 'bg-red-50/50 hover:bg-red-50 opacity-75'
                          : 'hover:bg-amber-50 focus:bg-amber-50'
                        }
                      `}
                    >
                      {/* Société radiée — warning banner */}
                      {isClosed && (
                        <div className="flex items-center gap-1.5 mb-1.5 text-red-500 text-[11px] font-medium">
                          <AlertTriangle size={11} className="shrink-0" />
                          Société radiée
                        </div>
                      )}

                      {/* Company name + categorie badge */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`font-semibold text-sm leading-tight truncate ${isClosed ? 'text-slate-500 line-through decoration-red-300' : 'text-slate-800'}`}>
                          {company.nom_complet}
                        </p>
                        {company.categorie_entreprise && (
                          <CategorieBadge code={company.categorie_entreprise} />
                        )}
                      </div>

                      {/* City + SIREN row */}
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {city && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <MapPin size={11} className="shrink-0 text-slate-400" />
                            {postalCode} {city}
                          </span>
                        )}
                        <span className="text-xs text-slate-400 font-mono tracking-wide">
                          SIREN {formatSiren(company.siren)}
                        </span>
                      </div>

                      {/* Activity label */}
                      {company.libelle_activite_principale && (
                        <p className="mt-0.5 text-xs text-slate-400 truncate">
                          {company.activite_principale} — {company.libelle_activite_principale}
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Footer hint */}
          {results.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <p className="text-xs text-slate-400">
                Source :{' '}
                <span className="font-medium text-slate-500">
                  Annuaire des Entreprises — data.gouv.fr
                </span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CompanyAutocomplete;
